import ActionResponse from './ActionResponse';
import { setAutoInvokeActionAlreadyRan } from '../actions/cardActions';
import downloadThemeDefinitions from '../service/downloadThemeDefinitions';
import resolveTheme from '../service/resolveTheme';
import ActionProvider from '../store/schema/ActionProvider';
import CardDetails from '../store/schema/CardDetails';
import ExecuteActionResponse from '../store/schema/ExecuteActionResponse';
import OutlookHostConfig from '../store/schema/OutlookHostConfig';
import PerfMarker from '../store/schema/PerfMarker';
import LoggingHandler from '../store/schema/LoggingHandler';
import Theme from '../store/schema/Theme';
import ThemeLoadStatus from '../store/schema/ThemeLoadStatus';
import ActionableMessageCardStrings from '../store/schema/ActionableMessageCardStrings';
import store from '../store/Store';
import applyMessageCardStyling from '../utils/applyMessageCardStyling';
import { getMessageCardCompactHostConfig, getMessageCardHostConfig } from '../utils/getHostConfig';
import initializeActionableMessageLibrary from '../utils/initializeActionableMessageLibrary';
import parseCardFromStringOrObject from '../utils/parseCardFromStringOrObject';
import parseMessageCard from '../utils/parseMessageCard';
import setHostCapabilitiesOnHostConfig from '../utils/setHostCapabilitiesOnHostConfig';
import * as Adaptive from 'adaptivecards';
import CustomAdaptiveCard from '../controls/CustomAdaptiveCard';
import CustomShowCardAction from '../cardActions/CustomShowCardAction';
import { observer } from 'mobx-react';
import { Label } from '@fluentui/react/lib/Label';
import { Spinner, SpinnerType } from '@fluentui/react/lib/Spinner';
import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import ActionableMessageContentHandler from '../store/schema/ActionableMessageContentHandler';
import { ActionableCardViewState } from '../store/schema/ActionableCardViewState';
import ResponseMessage, { MessageType, MessageViewType } from '../store/schema/ResponseMessage';
import { addCard, removeCard } from '../actions/cardActions';
import {
    ACTION_ANCHOR_PREFIX,
    COMPACT_MESSAGE_CARD_STYLE,
    CARD_ELEMENT_ID_PREFIX,
    CARD_CONTAINER_DEFAULT_MAX_WIDTH,
    CARD_CONTAINER_DEFAULT_ALIGNMENT,
    ACTION_CARD_EXPANDED,
    ACTION_CARD_COLLAPSED,
    MESSAGE_CARD_RENDERED,
    MESSAGE_CARD_HIDDEN,
    MESSAGE_CARD_REMOVED,
    AUTHENTICATION_FLOW_CLIENT_TYPE,
    AUTHENTICATION_WINDOW_CHECK_CLOSED_INTERVAL,
    AUTHENTICATION_WINDOW_CHECK_TIMEOUT,
    HttpStatusCode,
    HTTP_POST_ACTION_EXECUTED,
} from '../constants';
import { trace } from 'owa-trace';
import { getGuid } from 'owa-guid';
import * as React from 'react';
import addDefaultActionProvider from '../utils/addDefaultActionProvider';
import {
    showActionableCard,
    showActionResponse,
    showActionInProgress,
} from '../utils/renderChecks';
import { ActionableMessageTelemetryHandler } from '../utils/ActionableMessageTelemetryHandler';
import ElementDetails from '../store/schema/ElementDetails';
import DebugConstants from '../utils/DebugConstants';
import getAuthenticationWindowProperties from '../utils/getAuthenticationWindowProperties';
import { isFeatureEnabled } from 'owa-feature-flags';

require('../styles/globalCompactMessageCardStyles.scss');
const styles = require('../styles/cardStyles.scss');

export interface ActionableMessageCardWrapperProps {
    cardDetails: CardDetails; // The CardDetails containing information about the adaptive/message card payload from the item or elsewhere
    snackBarTimeout: number;
    telemetryHandler?: ActionableMessageTelemetryHandler;
    loggingHandler: LoggingHandler;
    showMessageBodyCallback?: (showBody: boolean) => void; // Callback to call to either show or hide the message body (in the case where the card is hosted in the reading pane)
    contentHandlerUndoMode?: boolean; // Whether or not the dark mode content handler should be undone
    actionProviders?: ActionProvider[]; // List of ActionProviders that register & execute custom Adaptive.Actions
    adjustCardElementBeforeRenderCallback?: (cardElement: HTMLElement) => void; // Callback once the card html is prepared, but before it is rendered so that the host can make adjustments to the DOM (e.g. fix inline images)
    onCardRendered?: (cardElement: HTMLElement) => void; // Callback to call once the card is rendered
    downloadThemeCallback?: (colorScheme: string) => Promise<{ [index: string]: Theme }>; // Callback to download Themes
    onCardDetailsUpdated?: (cardDetails: CardDetails[]) => void; // Callback to call when the card details have been updated to cause a re-render
    getClientAuthToken?: () => Promise<string>;
    actionableMessageContentHandler?: ActionableMessageContentHandler;
    actionableMessageCardStrings?: ActionableMessageCardStrings;
    isTrustedDomain?: (domain: string) => boolean;
}

export interface ActionableMessageCardWrapperState {
    cardState: ActionableCardViewState;
}

@observer
export default class ActionableMessageCardWrapper extends React.Component<
    ActionableMessageCardWrapperProps,
    ActionableMessageCardWrapperState
> {
    private adaptiveCard: CustomAdaptiveCard;
    private currentCardElement: HTMLElement;
    private showCardOnFailure: boolean;
    private inlineActionDefinitions: { [index: string]: any };
    private cardDetails: CardDetails;
    private cardContainer: HTMLElement;
    private actionProviders: ActionProvider[];
    private currentHostConfig: OutlookHostConfig;
    private pendingCardToShow: HTMLElement;
    private responseMessage: ResponseMessage;
    private cardElementId: string;
    private isRetryHttpActionFlow: Boolean = false;
    private authenticationUrl: string;
    private previousHttpAction: Adaptive.HttpAction;
    private previousHttpResponse: ExecuteActionResponse = null;
    private authenticationWindow: Window = null;
    private authenticationWindowTimeout: ReturnType<typeof setTimeout> = null;

    constructor(props: ActionableMessageCardWrapperProps) {
        super(props);
        this.state = {
            cardState: ActionableCardViewState.HideCard,
        };
        this.initializeCardProperties();
    }

    private initializeCardProperties = () => {
        const { actionProviders, actionableMessageCardStrings } = this.props;
        this.responseMessage = {
            message: '',
            type: MessageType.Error,
            viewType: MessageViewType.AutoDismiss,
        };
        this.actionProviders = addDefaultActionProvider(
            actionProviders,
            this.logTelemetryForAction
        );
        this.cardElementId = CARD_ELEMENT_ID_PREFIX + getGuid();
        initializeActionableMessageLibrary(this.actionProviders, actionableMessageCardStrings);
    };

    private logTelemetryForAction = (
        actionEventName: string,
        elementDetails: ElementDetails
    ): void => {
        const { telemetryHandler, cardDetails } = this.props;
        telemetryHandler.logTelemetryProvider(
            cardDetails,
            actionEventName,
            null /*actionContext*/,
            elementDetails
        );
    };

    private cancelAuthenticationFlow = (
        notShowCardOrDisplayMessage: boolean = false,
        message?: string
    ) => {
        trace.info(
            '[ActionableMessageDebug] ActionableMessageCardWrapper cancelAuthenticationFlow'
        );
        this.closeAuthenticationWindow();

        if (!notShowCardOrDisplayMessage) {
            const displayMessage = message || this.previousHttpResponse?.displayMessage;
            if (displayMessage) {
                trace.info(
                    '[ActionableMessageDebug] ActionableMessageCardWrapper cancelAuthenticationFlow: Showing error message'
                );
                this.displayActionStatusMessage(displayMessage, MessageType.Error);
            } else {
                this.setState({
                    cardState: ActionableCardViewState.ShowCard,
                });
            }
        }
        this.clearActionRetryData();
    };

    private closeAuthenticationWindow = () => {
        if (!this.authenticationWindow) {
            return;
        }
        trace.info(
            '[ActionableMessageDebug] ActionableMessageCardWrapper closeAuthenticationWindow'
        );
        clearTimeout(this.authenticationWindowTimeout);
        this.authenticationWindow.close();
    };

    private handlePostMessage = async event => {
        const origin = event.origin;
        if (!this.props.isTrustedDomain || !this.props.isTrustedDomain(origin)) {
            return;
        }
        const messageString = event.data;
        if (!messageString) {
            return;
        }
        let messageData = null;
        try {
            messageData = JSON.parse(messageString);
        } catch (e) {
            trace.info(
                '[ActionableMessageDebug] ActionableMessageCardWrapper handlePostMessage: Unable to parse message data'
            );
        }
        if (!messageData) {
            return;
        }

        const type = messageData.type;
        trace.info(
            '[ActionableMessageDebug] ActionableMessageCardWrapper handlePostMessage: Received event of type: ' +
                type
        );
        switch (type) {
            case 'ActionableMessagePreConsentCancelled':
                this.cancelAuthenticationFlow();
                break;

            case 'ActionableMessageListeningForToken':
                if (this.authenticationWindow && this.props.getClientAuthToken) {
                    let token = await this.props.getClientAuthToken();
                    if (token) {
                        const data = {
                            type: 'ActionableMessageAuthenticateUsingToken',
                            data: {
                                authToken: token,
                            },
                        };
                        trace.info(
                            '[ActionableMessageDebug] ActionableMessageCardWrapper handlePostMessage: Posting auth token to iframe'
                        );
                        this.authenticationWindow.postMessage(JSON.stringify(data), origin);
                    } else {
                        trace.info(
                            '[ActionableMessageDebug] ActionableMessageCardWrapper handlePostMessage: token is empty'
                        );
                        this.cancelAuthenticationFlow(
                            false /* notShowCardOrDisplayMessage */,
                            this.props.actionableMessageCardStrings.error.generic
                        );
                    }
                }
                break;

            case 'ActionableMessagePostAuthenticate':
                this.closeAuthenticationWindow();
                if (messageData?.data?.status === 'Success') {
                    trace.info(
                        '[ActionableMessageDebug] ActionableMessageCardWrapper handlePostMessage: Retrying execute action'
                    );
                    this.isRetryHttpActionFlow = true;
                    this.executeAction(this.previousHttpAction, false /* isAutoInvoke */);
                } else {
                    this.cancelAuthenticationFlow();
                }
                break;
        }
    };

    componentDidMount() {
        this.props.loggingHandler.log(DebugConstants.ComponentDidMount);
        trace.info('[ActionableMessageDebug] ActionableMessageCardWrapper mount.');
        this.tryUpdateCardElement(this.props.cardDetails);
        window.addEventListener('message', this.handlePostMessage);
    }

    componentDidUpdate(prevProps: ActionableMessageCardWrapperProps) {
        const { actionableMessageContentHandler, contentHandlerUndoMode, cardDetails } = this.props;
        this.props.loggingHandler.log(
            `${DebugConstants.ComponentDidUpdate}:${
                this.cardDetails ? this.cardDetails.cardId?.slice(0, 8) : ' '
            }`
        );
        trace.info(
            '[ActionableMessageDebug] ActionableMessageCardWrapper update. cardId: ' +
                this.cardDetails.cardId
        );
        if (
            contentHandlerUndoMode !== prevProps.contentHandlerUndoMode ||
            actionableMessageContentHandler !== prevProps.actionableMessageContentHandler
        ) {
            if (contentHandlerUndoMode) {
                actionableMessageContentHandler && actionableMessageContentHandler.onUndo();
            } else if (this.currentCardElement) {
                actionableMessageContentHandler &&
                    actionableMessageContentHandler.onApply(this.currentCardElement);
            }
        }
        if (cardDetails !== prevProps.cardDetails) {
            this.props.loggingHandler.log(DebugConstants.ComponentDidUpdateUpdate);
            trace.info(
                '[ActionableMessageDebug] ActionableMessageCardWrapper update and updateCardElement. cardId: ' +
                    this.cardDetails.cardId
            );

            this.tryUpdateCardElement(cardDetails, false /* allowAutoInvokeActions */);
        }
    }

    componentWillUnmount() {
        this.props.loggingHandler.log(
            `${DebugConstants.ComponentWillUnmount}:${
                this.cardDetails ? this.cardDetails.cardId?.slice(0, 8) : ' '
            }`
        );
        trace.info('[ActionableMessageDebug] ActionableMessageCardWrapper unmount.');

        if (this.cardDetails) {
            removeCard(this.cardDetails.cardId);
        }

        if (this.currentCardElement) {
            this.cleanupCard();
        }

        this.cancelAuthenticationFlow(true /* notShowCardOrDisplayMessage */);

        window.removeEventListener('message', this.handlePostMessage);
    }

    render() {
        this.props.loggingHandler.log(DebugConstants.Render);
        trace.info('[ActionableMessageDebug] ActionableMessageCardWrapper render');
        const { cardState } = this.state;

        return (
            showActionableCard(cardState) && (
                <div
                    className={`${styles.container} allowTextSelection`}
                    ref={r => this.setCardContainer(r)}>
                    {showActionResponse(cardState, this.responseMessage) &&
                        this.renderActionResponse()}
                    {showActionInProgress(cardState) && this.renderActionSpinner()}
                </div>
            )
        );
    }

    private isValidCardDetails = (): boolean => {
        return !!this.cardDetails && !!this.cardDetails.cardId;
    };

    private renderActionResponse = (): JSX.Element => {
        return (
            this.isValidCardDetails() && (
                <ActionResponse
                    message={this.responseMessage.message}
                    type={this.responseMessage.type}
                    viewType={this.responseMessage.viewType}
                    autoDismissTimeout={this.props.snackBarTimeout}
                    onHideActionResponse={this.setShowCard}
                    acknowledgeButtonText={this.responseMessage.acknowledgeButtonText}
                    onAcknowledgeActionResponse={this.responseMessage.onAcknowledgeActionResponse}
                />
            )
        );
    };

    private renderActionSpinner = (): JSX.Element => {
        return (
            this.isValidCardDetails() && (
                <div className={styles.actionInProgressOverlay}>
                    <Spinner type={SpinnerType.large} className={styles.actionInProgressSpinner} />
                    <Label className={styles.actionInProgressLabel}>
                        {this.props.actionableMessageCardStrings.information.actionInProgress}
                    </Label>
                </div>
            )
        );
    };

    private setCardContainer = (container: HTMLElement) => {
        if (!this.cardContainer) {
            this.props.loggingHandler.log(DebugConstants.SetCardContainer);
            trace.info('[ActionableMessageDebug] ActionableMessageCardWrapper: setCardContainer');
            this.cardContainer = container;

            if (this.pendingCardToShow) {
                this.addCardElementToDOM(this.pendingCardToShow);
            }
        }
    };

    private removeCardElementFromDOM = () => {
        const oldCard = document.getElementById(this.cardElementId);
        if (oldCard && this.cardContainer && this.cardContainer.contains(oldCard)) {
            this.props.loggingHandler.log(DebugConstants.RemoveCardElement);
            trace.info(
                '[ActionableMessageDebug] ActionableMessageCard: removing old card from container'
            );
            this.cardContainer.removeChild(oldCard);
            this.currentCardElement = null;
        }
    };

    private addCardElementToDOM = (cardElement: HTMLElement) => {
        if (this.cardContainer) {
            this.pendingCardToShow = null;

            // First remove the old card (if any)
            this.removeCardElementFromDOM();

            // And now add the current card
            this.props.loggingHandler.log(DebugConstants.AddCardElement);
            trace.info(
                '[ActionableMessageDebug] ActionableMessageCardWrapper: adding card element to DOM'
            );
            this.currentCardElement = cardElement;
            this.applyConstrainWidthToCardContainer();
            this.cardContainer.appendChild(this.currentCardElement);
        } else {
            this.pendingCardToShow = cardElement;
        }
    };

    private getTheme = async (cardDetails: CardDetails): Promise<Theme> => {
        const { telemetryHandler, downloadThemeCallback } = this.props;
        if (
            !isFeatureEnabled('rp-actionableMessagesThemeConfig') &&
            store.themeStore.loadStatus === ThemeLoadStatus.Unloaded
        ) {
            const loadThemeDefinitionsPerf = new PerformanceDatapoint(
                'MCActionableCardLoadThemeDefinitions'
            );
            telemetryHandler.stampPerfCheckpoint(PerfMarker.ThemeDefinitionsLoadStart);
            await downloadThemeDefinitions(downloadThemeCallback);
            loadThemeDefinitionsPerf.end();
            telemetryHandler.stampPerfCheckpoint(PerfMarker.ThemeDefinitionsLoadEnd);
        }

        const { card } = this.cardDetails;
        const resolveThemeFile = new PerformanceDatapoint('MCActionableCardResolveThemeFile');
        telemetryHandler.stampPerfCheckpoint(PerfMarker.ThemeResolveStart);
        const theme = await resolveTheme(card.theme ? card.theme : 'default');
        resolveThemeFile.end();
        telemetryHandler.stampPerfCheckpoint(PerfMarker.ThemeResolveEnd);

        return theme;
    };

    private setAdaptiveCardHandlers = (): void => {
        const { actionableMessageContentHandler, telemetryHandler, cardDetails } = this.props;

        this.adaptiveCard.onExecuteAction = (action: Adaptive.Action) => {
            this.executeAction(action, false /* isAutoInvoke */);
        };

        this.adaptiveCard.onInlineCardExpanded = (
            action: CustomShowCardAction,
            isExpanded: boolean
        ) => {
            // When an inline card is expanded, new elements are generated and added to the DOM. Process the newly added card for Dark Mode
            actionableMessageContentHandler &&
                actionableMessageContentHandler.onApply(action.card.renderedElement);
            // When an inline card is expanded, scroll so the user can see the action button
            const elem = document.getElementById(this.cardElementId);
            elem && elem.scrollIntoView(true);
            const elementDetails: ElementDetails = {
                id: action.id,
                type: action.getJsonTypeName(),
            };
            telemetryHandler.logTelemetryProvider(
                cardDetails,
                isExpanded ? ACTION_CARD_EXPANDED : ACTION_CARD_COLLAPSED,
                null /*actionContext*/,
                elementDetails
            );
        };
    };

    private tryUpdateCardElement = async (
        cardDetails: CardDetails,
        allowAutoInvokeActions: boolean = true
    ) => {
        try {
            this.updateCardElement(cardDetails, allowAutoInvokeActions);
        } catch (error) {
            this.props.loggingHandler.log(DebugConstants.TryUpdateCardElementException);
            trace.warn(
                '[ActionableMessageDebug] ActionableMessageCardWrapper: exception encountered during updateCardElement: ' +
                    error.message
            );
            this.setHideCard(true /* wasError */);
        }
    };

    private updateCardElement = async (
        updatedCardDetails: CardDetails,
        allowAutoInvokeActions: boolean = true
    ) => {
        const {
            actionableMessageContentHandler,
            telemetryHandler,
            adjustCardElementBeforeRenderCallback,
            cardDetails,
        } = this.props;

        this.props.loggingHandler.log(
            `${DebugConstants.UpdateCardElementStart}:${
                updatedCardDetails ? updatedCardDetails.cardId?.slice(0, 8) : ' '
            }`
        );
        trace.info(
            '[ActionableMessageDebug] ActionableMessageCardWrapper updateCardElement: start. cardId: ' +
                updatedCardDetails.cardId
        );

        // Save the new card details
        if (this.cardDetails != updatedCardDetails) {
            this.cardDetails = updatedCardDetails;
            // Ensure the card has an entry in the store to track its state
            if (!store.cards.has(this.cardDetails.cardId)) {
                addCard(this.cardDetails.cardId);
            }
        } else {
            this.props.loggingHandler.log(DebugConstants.UpdateCardElementSkippingRender);
            trace.info(
                '[ActionableMessageDebug] ActionableMessageCardWrapper updateCardElement: card details unchanged. Skipping re-render. cardId: ' +
                    updatedCardDetails.cardId
            );
            return;
        }

        telemetryHandler.stampPerfCheckpoint(
            PerfMarker.LoadStart,
            telemetryHandler.perfData.loadStartTime
        );

        telemetryHandler.setTotalFetchTimeForCard();
        telemetryHandler.stampPerfCheckpoint(PerfMarker.CardRenderStart);

        const loadThemeDatapoint = new PerformanceDatapoint('MCActionableCardLoadThemeTotal');
        telemetryHandler.stampPerfCheckpoint(PerfMarker.ThemeLoadTotalStart);

        // Load the theme for the new card
        const currentTheme = await this.getTheme(this.cardDetails);
        if (currentTheme) {
            this.currentHostConfig = currentTheme.hostConfig;
            loadThemeDatapoint.end();
            telemetryHandler.stampPerfCheckpoint(PerfMarker.ThemeLoadTotalEnd);
        } else {
            loadThemeDatapoint.endWithError(DatapointStatus.ClientError);
            this.props.loggingHandler.log(DebugConstants.UpdateCardElementThemeError);
            trace.warn(
                '[ActionableMessageDebug] ActionableMessageCardWrapper updateCardElement: Error loading theme for card'
            );
            this.setHideCard(true /* wasError */);
            return;
        }

        // Extract the card json, and create the card element based on it
        const cardJson = this.cardDetails.card;
        if (cardJson) {
            const initializeLibraryDatapoint = new PerformanceDatapoint(
                'MCActionableCardInitializeCardLibrary'
            );
            telemetryHandler.stampPerfCheckpoint(PerfMarker.CardCreateStart);

            if (this.cardDetails.isAdaptiveCard) {
                this.initializeAdaptiveCard(cardJson, currentTheme);
            } else {
                this.initializeMessageCard(cardJson);
            }
            initializeLibraryDatapoint.end();
            telemetryHandler.stampPerfCheckpoint(PerfMarker.CardCreateEnd);

            const shouldHide = this.processAutoInvokeActions(cardJson, allowAutoInvokeActions);
            if (!shouldHide) {
                this.setAdaptiveCardHandlers();

                telemetryHandler.stampPerfCheckpoint(PerfMarker.CardLibraryRenderStart);

                const newCardElement = this.adaptiveCard.render();

                if (newCardElement) {
                    this.props.loggingHandler.log(
                        DebugConstants.UpdateCardElementNewCardElementCreated
                    );
                    trace.info(
                        '[ActionableMessageDebug] ActionableMessageCardWrapper updateCardElement: newCardElement is created. cardId: ' +
                            cardDetails.cardId
                    );
                    telemetryHandler.stampPerfCheckpoint(PerfMarker.CardLibraryRenderEnd);

                    const prepareCardDatapoint = new PerformanceDatapoint(
                        'MCActionableCardPrepareCard'
                    );
                    telemetryHandler.stampPerfCheckpoint(PerfMarker.PrepareCardStart);

                    newCardElement.id = this.cardElementId;
                    this.applySpecialStyling(cardJson, newCardElement);

                    if (adjustCardElementBeforeRenderCallback) {
                        adjustCardElementBeforeRenderCallback(newCardElement);
                    }

                    this.processInlineActions(newCardElement);
                    this.updateParentProperties(cardJson);
                    actionableMessageContentHandler &&
                        actionableMessageContentHandler.onApply(newCardElement);

                    prepareCardDatapoint.end();
                    telemetryHandler.stampPerfCheckpoint(PerfMarker.PrepareCardEnd);

                    // Finish card update and set visibility
                    this.props.loggingHandler.log(
                        DebugConstants.UpdateCardElementAddNewCardElement
                    );
                    trace.info(
                        '[ActionableMessageDebug] ActionableMessageCardWrapper updateCardElement: adding newCardElement to the DOM. cardId: ' +
                            cardDetails.cardId
                    );
                    this.addCardElementToDOM(newCardElement);
                    this.setShowCard();
                }
                telemetryHandler.stampPerfCheckpoint(PerfMarker.CardRenderEnd);

                if (this.cardDetails) {
                    telemetryHandler.logTelemetryProvider(cardDetails, MESSAGE_CARD_RENDERED);
                }
            } else {
                this.setHideCard(false /* wasError */);
            }
        } else {
            this.setHideCard(true /* wasError */);
        }
    };

    private setHideCard = (wasError: boolean) => {
        this.props.loggingHandler.log(`${DebugConstants.HideCard}:${wasError ? 1 : 0}`);
        trace.info(
            `[ActionableMessageDebug] hideCard. wasError: ${wasError}. cardId: ${this.cardDetails.cardId}`
        );

        this.props.showMessageBodyCallback && this.props.showMessageBodyCallback(true);
        if (wasError) {
            /**
             * Possible errors:
             * 1. Card data could be empty
             * 2. Exception while trying to update the card element with the payload
             * 3. Unable to retrieve the theme definition of the card
             */
            this.setState({
                cardState: ActionableCardViewState.HideCardWithError,
            });
        } else {
            this.setState({
                cardState: ActionableCardViewState.HideCard,
            });
        }
    };

    private setShowCard = () => {
        this.props.loggingHandler.log(`${DebugConstants.ShowCard}`);
        trace.info(
            '[ActionableMessageDebug] ActionableMessageCardWrapper setCardVisibility: setting card visibility to true '
        );
        this.setState({
            cardState: ActionableCardViewState.ShowCard,
        });
        /// need to send the callback with every showCard state change
        this.props.onCardRendered && this.props.onCardRendered(this.currentCardElement);
    };

    private cleanupCard = () => {
        const { actionableMessageContentHandler } = this.props;

        this.props.loggingHandler.log(
            `${DebugConstants.CleanupCard}:${
                this.cardDetails ? this.cardDetails?.cardId.slice(0, 8) : ' '
            }`
        );
        trace.info(
            '[ActionableMessageDebug] ActionableMessageCardWrapper cleanupCard: cardId: ' +
                this.cardDetails.cardId
        );
        this.detachInlineActionAnchorHandlers(this.currentCardElement);
        if (this.adaptiveCard) {
            this.adaptiveCard.getAllInputs().forEach(input => {
                input.remove();
            });
        }

        this.currentHostConfig = null;
        actionableMessageContentHandler && actionableMessageContentHandler.onUndo();
    };

    private initializeAdaptiveCard = (cardJson: any, theme: Theme) => {
        this.adaptiveCard = new CustomAdaptiveCard();
        this.adaptiveCard.hostConfig = theme.hostConfig;
        setHostCapabilitiesOnHostConfig(
            this.adaptiveCard.hostConfig as Adaptive.HostConfig,
            this.actionProviders
        );

        this.adaptiveCard.parse(cardJson);

        // To work around a card compatibility issue in Outlook desktop, some cards will be marked with version 1.01 when they are
        // actually just 1.0 cards with a Transaction action. OWA's adaptive card 1.0 supports this action type, so until we update
        // the adaptive library to >=1.01 we'll just fake it here to get the renderer to play nice.
        // Once the adaptive library is updated to >=1.01 this should be removed. OW: 39376
        if (cardJson.version === '1.01') {
            trace.info('Card is requesting version 1.01; forcing version to 1.0 for rendering');
            this.adaptiveCard.version = new Adaptive.Version(1, 0);
        }

        if (theme.stylesheetUrl) {
            setThemeStylesheet(cardJson.theme, theme.stylesheetUrl);
        }
        this.processInlineActionDefinitions(cardJson);
    };

    private initializeMessageCard = (cardJson: any) => {
        this.adaptiveCard = parseMessageCard(cardJson, this.actionProviders);
        if (cardJson.style === COMPACT_MESSAGE_CARD_STYLE) {
            this.adaptiveCard.hostConfig = getMessageCardCompactHostConfig();
        } else {
            this.adaptiveCard.hostConfig = getMessageCardHostConfig();
        }
    };

    private applySpecialStyling = (cardJson: any, cardElement: HTMLElement) => {
        // There is some styling needed by legacy message cards that isn't handled by the adaptive card library,
        // so manually apply those styles now
        if (!this.cardDetails.isAdaptiveCard) {
            applyMessageCardStyling(cardElement, cardJson);
        }

        // Set style for compact message cards
        if (
            cardJson.style === COMPACT_MESSAGE_CARD_STYLE ||
            cardJson.theme === COMPACT_MESSAGE_CARD_STYLE
        ) {
            cardElement.className = styles.compactCard;
        }

        // If custom theme is present set theme name on card container
        const theme =
            cardJson.theme && cardJson.theme !== COMPACT_MESSAGE_CARD_STYLE
                ? cardJson.theme
                : 'none';
        cardElement.setAttribute('data-ac-theme', theme);
    };

    private processAutoInvokeActions = (
        cardJson: any,
        allowAutoInvokeActions: boolean
    ): boolean => {
        const { telemetryHandler } = this.props;
        let shouldHide: boolean = false;
        const card = store.cards.get(this.cardDetails.cardId);

        if (cardJson.autoInvokeAction) {
            this.props.loggingHandler.log(
                `${DebugConstants.ProcessAutoInvokeActionsHasAutoInvoke}:${
                    this.cardDetails ? this.cardDetails.cardId?.slice(0, 8) : ' '
                }`
            );
            trace.info(
                '[ActionableMessageDebug] ActionableMessageCardWrapper processAutoInvokeActions: Card does have auto invoke action. cardId: ' +
                    this.cardDetails.cardId
            );

            const enableAutoInvoke =
                allowAutoInvokeActions && (!card || !card.autoInvokeActionAlreadyRan);

            if (enableAutoInvoke) {
                if (cardJson.autoInvokeAction.hideCardOnInvoke) {
                    this.props.loggingHandler.log(
                        DebugConstants.ProcessAutoInvokeActionsHideCardOnInvoke
                    );
                    trace.info(
                        '[ActionableMessageDebug] ActionableMessageCardWrapper processAutoInvokeActions: HideCardOnInvoke is true, so card will be hidden cardId: ' +
                            this.cardDetails.cardId
                    );
                    shouldHide = true;

                    // If we're going to hide the message card when the auto invoke action is running then we're going to need
                    // to show something, so show the message body in this case
                    this.showMessageBody();
                }

                if (cardJson.autoInvokeOptions) {
                    this.showCardOnFailure = cardJson.autoInvokeOptions.showCardOnFailure;
                }

                telemetryHandler.stampPerfCheckpoint(PerfMarker.AutoInvokeActionStart);

                setAutoInvokeActionAlreadyRan(this.cardDetails.cardId, true);

                if (!this.cardDetails.cardId) {
                    this.props.loggingHandler.log(
                        DebugConstants.ProcessAutoInvokeActionsCardIdUndefined
                    );
                }

                this.props.loggingHandler.log(DebugConstants.ProcessAutoInvokeActionsExecuting);
                trace.info(
                    '[ActionableMessageDebug] ActionableMessageCardWrapper processAutoInvokeActions: Executing auto invoke action for cardId: ' +
                        this.cardDetails.cardId
                );
                this.executeAutoInvokeAction(cardJson.autoInvokeAction);
            } else {
                this.props.loggingHandler.log(DebugConstants.ProcessAutoInvokeActionsException);
                trace.info(
                    '[ActionableMessageDebug] ActionableMessageCardWrapper processAutoInvokeActions: Auto invoke actions not allowed for card. cardId: ' +
                        this.cardDetails.cardId
                );
            }
        }

        return shouldHide;
    };

    private showMessageBody = () => {
        if (this.props.showMessageBodyCallback) {
            this.props.showMessageBodyCallback(true);
        }
    };

    private hideMessageBody = () => {
        if (this.props.showMessageBodyCallback) {
            this.props.showMessageBodyCallback(false);
        }
    };

    private processInlineActions = (cardElement: HTMLElement) => {
        if (this.inlineActionDefinitions) {
            this.attachInlineActionAnchorHandlers(cardElement);
        }
    };

    private updateParentProperties = (cardJson: any) => {
        // Show/hide the original message body
        if (cardJson.style === COMPACT_MESSAGE_CARD_STYLE || !cardJson.hideOriginalBody) {
            this.showMessageBody();
        } else if (cardJson.hideOriginalBody) {
            this.hideMessageBody();
        }
    };

    private processInlineActionDefinitions = (parsedCardJson: any) => {
        this.inlineActionDefinitions = {};
        if (parsedCardJson.resources?.actions) {
            const actions: Array<any> = parsedCardJson.resources.actions;
            for (let i = 0; i < actions.length; i++) {
                this.inlineActionDefinitions[actions[i].id] = actions[i];
            }
        }
    };

    private executeAction = (action: Adaptive.Action, isAutoInvoke: boolean): boolean => {
        if (!action || !this.adaptiveCard) {
            return false;
        }

        if (!isAutoInvoke && action instanceof Adaptive.HttpAction) {
            this.setState({ cardState: ActionableCardViewState.ActionInProgress });

            /**
             * Making sure we preserve the HttpAction before executing it, in case if we end up in retrying the action
             */
            if (!this.isRetryHttpActionFlow) {
                this.previousHttpAction = action;
            }
        }

        let actionHandled = false;
        for (let i = 0; i < this.actionProviders.length; i++) {
            const actionProvider = this.actionProviders[i];
            actionHandled = actionProvider.executeAction(
                action,
                this.cardDetails,
                getAllInputs(action) || [],
                response => this.handleActionResponse(response, isAutoInvoke),
                this.props.telemetryHandler.messageContext,
                this.props.telemetryHandler.telemetryProvider,
                isAutoInvoke
            );

            if (actionHandled) {
                break;
            }
        }

        return actionHandled;
    };

    private executeAutoInvokeAction = (actionDefinition: any) => {
        if (actionDefinition) {
            let actionExecuted = false;
            for (let i = 0; i < this.actionProviders.length; i++) {
                const actionProvider = this.actionProviders[i];
                if (actionProvider.processAutoInvokeAction) {
                    const action = actionProvider.processAutoInvokeAction(
                        actionDefinition,
                        this.cardDetails
                    );
                    if (action) {
                        this.props.loggingHandler.logAutoInvokeExecution();
                        this.executeAction(action, true /* isAutoInvoke */);
                        actionExecuted = true;
                        break;
                    }
                }
            }

            if (!actionExecuted) {
                this.props.loggingHandler.log(DebugConstants.ExecuteAutoInvokeActionNoProvider);
                trace.warn(
                    '[ActionableMessageDebug] ActionableMessageCardWrapper executeAutoInvokeAction: no action provider supplied that can process auto invoke actions'
                );
            }
        }
    };

    private attachInlineActionAnchorHandlers = (cardElement: HTMLElement) => {
        const actionAnchors = cardElement.querySelectorAll(
            'a[href^="' + ACTION_ANCHOR_PREFIX + '"]'
        );
        if (actionAnchors) {
            for (let i = 0; i < actionAnchors.length; i++) {
                const anchor = actionAnchors.item(i) as HTMLElement;
                anchor.addEventListener('click', this.onInlineActionAnchorClicked);
            }
        }
    };

    private detachInlineActionAnchorHandlers = (cardElement: HTMLElement) => {
        if (!cardElement) {
            return;
        }

        const actionAnchors = cardElement.querySelectorAll(
            'a[href^="' + ACTION_ANCHOR_PREFIX + '"]'
        );
        if (actionAnchors) {
            for (let i = 0; i < actionAnchors.length; i++) {
                const anchor = actionAnchors.item(i) as HTMLElement;
                anchor.removeEventListener('click', this.onInlineActionAnchorClicked);
            }
        }
    };

    private handleActionResponse = (response: ExecuteActionResponse, isAutoInvoke: boolean) => {
        this.props.loggingHandler.log(
            `${DebugConstants.HandleActionResponseStart}:${
                this.cardDetails ? this.cardDetails.cardId?.slice(0, 8) : ' '
            }`
        );
        trace.info(
            '[ActionableMessageDebug] ActionableMessageCardWrapper handleActionResponse started. cardId: ' +
                this.cardDetails.cardId
        );

        const { telemetryHandler } = this.props;

        if (!isAutoInvoke) {
            telemetryHandler.logTelemetryProvider(this.cardDetails, HTTP_POST_ACTION_EXECUTED);
        }

        const { cardState } = this.state;

        if (cardState === ActionableCardViewState.HideCardWithError) {
            this.props.loggingHandler.log(DebugConstants.HandleActionResponseStopUpdate);
            trace.info(
                '[ActionableMessageDebug] ActionableMessageCardWrapper handleActionResponse: card was hidden because of a processing error, so stop update'
            );
            return;
        }

        /**
         * This benchmarks network call time to execute auto invoke action
         */
        if (isAutoInvoke) {
            telemetryHandler.stampPerfCheckpoint(PerfMarker.AutoInvokeActionEnd);
            telemetryHandler.updatePerfCheckpointsBeforeUpdatingAutoInvokeResponse();
        }

        this.previousHttpResponse = response;

        if (response.success) {
            this.handleActionSuccessResponse(response, isAutoInvoke);
        } else {
            this.handleActionErrorResponse(response, isAutoInvoke);
        }

        /**
         * Making sure we clean up all the debris after we have executed the action successfully or have retried once
         */
        if (this.isRetryHttpActionFlow || response.success) {
            this.clearActionRetryData();
        }
    };

    private handleActionSuccessResponse = (
        response: ExecuteActionResponse,
        isAutoInvoke: boolean
    ) => {
        this.props.loggingHandler.log(DebugConstants.HandleActionSuccessResponseStart);
        trace.info(
            '[ActionableMessageDebug] ActionableMessageCardWrapper handleActionSuccessResponse: Start'
        );

        const { telemetryHandler, onCardDetailsUpdated } = this.props;
        if (response.newCardPayload) {
            if (typeof onCardDetailsUpdated === 'function') {
                const newCardDetails = parseCardFromStringOrObject(
                    this.cardDetails,
                    response.newCardPayload,
                    response.newCardSignature,
                    undefined /* isAdaptive: leave this undefined so that it will determine if its adaptive by inspecting the json */,
                    this.cardDetails.cardId
                );

                this.props.loggingHandler.log(
                    DebugConstants.HandleActionSuccessResponseOnCardDetailsUpdated
                );
                trace.info(
                    '[ActionableMessageDebug] ActionableMessageCardWrapper handleActionSuccessResponse: New card payload available and onCardDetailsUpdated being called.'
                );
                onCardDetailsUpdated([newCardDetails]);
            }
        }

        if (this.isCardVisible()) {
            /**
             * In this case, we only need to look at whether the response says that we hide/remove the card
             * otherwise make sure we render action status message (NewCard would be rendered by onCardDetailsUpdated callback)
             */
            if (response.hideCard || response.removeCard) {
                telemetryHandler.logTelemetryProvider(
                    this.cardDetails,
                    response.hideCard ? MESSAGE_CARD_HIDDEN : MESSAGE_CARD_REMOVED
                );
                this.props.loggingHandler.log(DebugConstants.HandleActionSuccessResponseHideCard);
                trace.info(
                    '[ActionableMessageDebug] ActionableMessageCardWrapper handleActionSuccessResponse: Hiding card'
                );
                this.setHideCard(false /*wasError*/);
                this.showMessageBody();
            } else if (!isAutoInvoke) {
                trace.info(
                    '[ActionableMessageDebug] ActionableMessageCardWrapper handleActionSuccessResponse: Showing action result message'
                );
                this.displayActionStatusMessage(response.displayMessage, MessageType.Success);
            }
        } else {
            /**
             * In this case, we should check to see if the response either contains a new card, or tells us to show the original card
             */
            if (response.newCardPayload || response.showOriginalCard) {
                this.props.loggingHandler.log(DebugConstants.HandleActionSuccessResponseShowCard);
                trace.info(
                    '[ActionableMessageDebug] ActionableMessageCardWrapper handleActionSuccessResponse: Showing card'
                );
                this.setState({ cardState: ActionableCardViewState.ShowCard });
            } else {
                /**
                 * Making sure we show the message body, if we don't have anything to show at all
                 */
                this.setHideCard(false /*wasError*/);
                this.showMessageBody();
            }
        }
    };

    private handleActionErrorResponse = (
        response: ExecuteActionResponse,
        isAutoInvoke: boolean
    ) => {
        this.props.loggingHandler.log(DebugConstants.HandleActionErrorResponseStart);
        trace.info(
            '[ActionableMessageDebug] ActionableMessageCardWrapper handleActionErrorResponse: Start'
        );
        if (isAutoInvoke) {
            /**
             * In this case, we don't have to show any error message or do retry action
             * we does need to check if the auto-invoke options are set to hide the card on failure
             */
            if (this.showCardOnFailure === false) {
                this.props.loggingHandler.log(DebugConstants.HandleActionErrorResponseHideCard);
                trace.info(
                    '[ActionableMessageDebug] ActionableMessageCardWrapper handleActionErrorResponse: hiding card'
                );
                this.setHideCard(false /*wasError*/);
                this.showMessageBody();
            }
        } else {
            /**
             * Making sure we do not end up firing executeAction infinitely
             */
            if (
                !isFeatureEnabled('rp-actionableMessages-identity-linking') ||
                this.isRetryHttpActionFlow ||
                (response.status && response.status.toLowerCase() === 'completed')
            ) {
                this.props.loggingHandler.log(DebugConstants.HandleActionErrorResponseShowMessage);
                trace.info(
                    '[ActionableMessageDebug] ActionableMessageCardWrapper handleActionErrorResponse: displaying action message - either completed or retry failed'
                );
                this.displayActionStatusMessage(response.displayMessage, MessageType.Error);
            } else {
                let errorObj = null;
                if (response.errors && response.errors.length > 0) {
                    errorObj = response.errors[0];
                }
                /**
                 * Check whether error is in territory of Auth, if so we need to retry action after Auth flow completes
                 */
                if (this.shouldHandleAuthErrors(response, errorObj)) {
                    let url = null;
                    if (errorObj?.httpStatusCode === HttpStatusCode.Unauthorized && errorObj.url) {
                        url = errorObj.url;
                    } else if (response.authenticationUrl) {
                        url = response.authenticationUrl;
                    }
                    this.authenticationUrl = this.addAuthenticationUrlEssentials(url);
                    this.props.loggingHandler.log(
                        DebugConstants.HandleActionErrorResponseHandleAuth
                    );
                    trace.info(
                        '[ActionableMessageDebug] ActionableMessageCardWrapper handleActionErrorResponse: Handling authentication error'
                    );
                    const message = this.props.actionableMessageCardStrings.information
                        .authenticationFlowAcknowledgeMessage;
                    const buttonText = this.props.actionableMessageCardStrings.information
                        .authenticationFlowAcknowledgeButtonText;

                    this.displayActionStatusMessage(
                        message,
                        MessageType.Warning,
                        MessageViewType.Freeze,
                        buttonText,
                        this.handleAuthenticationFlow
                    );
                } else {
                    /**
                     * After we receive the action response, we are making sure to add generic success or error message
                     * See: executeHttpAction.ts -> parseResponseText
                     */
                    let displayMessage = response.displayMessage;
                    if (errorObj?.message) {
                        displayMessage = errorObj.message;
                    }
                    this.props.loggingHandler.log(
                        DebugConstants.HandleActionErrorResponseShowErrorMessage
                    );
                    trace.info(
                        '[ActionableMessageDebug] ActionableMessageCardWrapper handleActionErrorResponse: Display error message'
                    );
                    this.displayActionStatusMessage(displayMessage, MessageType.Error);
                }
            }
        }
    };

    private addAuthenticationUrlEssentials(url) {
        if (!url) {
            return url;
        }

        return `${url}&client=${AUTHENTICATION_FLOW_CLIENT_TYPE}`;
    }

    private clearActionRetryData = () => {
        trace.info(
            '[ActionableMessageDebug] ActionableMessageCardWrapper clearActionRetryData: Start'
        );
        this.isRetryHttpActionFlow = false;
        this.authenticationUrl = null;
        this.previousHttpAction = null;
        this.previousHttpResponse = null;
        this.authenticationWindow = null;
    };

    private checkAuthenticationWindow = (elapsedTime: number) => {
        return () => {
            if (!this.authenticationWindow) {
                return;
            }
            if (this.authenticationWindow.closed) {
                this.cancelAuthenticationFlow();
            } else if (elapsedTime < AUTHENTICATION_WINDOW_CHECK_TIMEOUT) {
                this.setAuthenticationWindowTimeout(
                    elapsedTime + AUTHENTICATION_WINDOW_CHECK_CLOSED_INTERVAL
                );
            } else {
                this.cancelAuthenticationFlow();
            }
        };
    };

    private setAuthenticationWindowTimeout = (elapsedTime: number) => {
        this.authenticationWindowTimeout = setTimeout(
            this.checkAuthenticationWindow(elapsedTime),
            AUTHENTICATION_WINDOW_CHECK_CLOSED_INTERVAL
        );
    };

    private handleAuthenticationFlow = () => {
        trace.info(
            '[ActionableMessageDebug] ActionableMessageCardWrapper handleAuthenticationFlow: Start'
        );

        this.setState({ cardState: ActionableCardViewState.ActionInProgress });

        const windowProps = getAuthenticationWindowProperties();
        this.authenticationWindow = window.open(this.authenticationUrl, '_blank', windowProps);
        if (this.authenticationWindow) {
            this.setAuthenticationWindowTimeout(0 /* elapsedTime */);
        } else {
            /**
             * Popup could have been blocked or something else went wrong
             */
            this.cancelAuthenticationFlow();
        }
    };

    private displayActionStatusMessage = (
        message: string,
        messageType: MessageType,
        viewType: MessageViewType = MessageViewType.AutoDismiss,
        acknowledgeButtonText: string = null,
        onAcknowledgeActionResponse: () => void = null
    ) => {
        this.responseMessage.message = message;
        this.responseMessage.type = messageType;
        this.responseMessage.viewType = viewType;
        this.responseMessage.acknowledgeButtonText = acknowledgeButtonText;
        this.responseMessage.onAcknowledgeActionResponse = onAcknowledgeActionResponse;

        this.setState({
            cardState: ActionableCardViewState.ShowActionsResponse,
        });
    };

    private shouldHandleAuthErrors(response, errorObj) {
        if (
            errorObj?.httpStatusCode &&
            (errorObj.httpStatusCode === HttpStatusCode.Unauthorized ||
                errorObj.httpStatusCode === HttpStatusCode.Forbidden)
        ) {
            return true;
        }

        if (response.authenticationUrl) {
            return true;
        }

        return false;
    }

    private isCardVisible = () => {
        const { cardState } = this.state;
        return (
            cardState !== ActionableCardViewState.HideCard &&
            cardState !== ActionableCardViewState.HideCardWithError
        );
    };

    private onInlineActionAnchorClicked = (evt: MouseEvent) => {
        if (!this.inlineActionDefinitions) {
            return;
        }

        const target = evt.target as HTMLAnchorElement;
        const id = target.href.substr(ACTION_ANCHOR_PREFIX.length);
        const customAction = this.inlineActionDefinitions[id];

        if (!customAction) {
            return;
        }

        evt.preventDefault();
        const action = this.parseCustomAction(customAction);
        this.executeAction(action, false /* isAutoInvoke */);
    };

    private parseCustomAction = (inlineAction: any): Adaptive.Action => {
        const action = Adaptive.AdaptiveCard.actionTypeRegistry.createInstance(inlineAction.type);
        action.parse(inlineAction);
        action.setParent(this.adaptiveCard);
        return action;
    };

    private applyConstrainWidthToCardContainer = () => {
        const { cardDetails } = this.props;

        if (cardDetails?.isAdaptiveCard && cardDetails.card.constrainWidth !== false) {
            let maxWidth: string = CARD_CONTAINER_DEFAULT_MAX_WIDTH;
            let align: string = CARD_CONTAINER_DEFAULT_ALIGNMENT;

            const hostConfig: any = this.currentHostConfig;
            if (hostConfig?.cardMaximumWidth) {
                maxWidth = hostConfig.cardMaximumWidth;
                if (typeof maxWidth === 'number') {
                    maxWidth = maxWidth + 'px';
                }
            }
            if (hostConfig?.cardHorizontalAlignment) {
                align = hostConfig.cardHorizontalAlignment;
            }
            if (hostConfig?.cardHostBackgroundColor && this.cardContainer.parentElement) {
                this.cardContainer.parentElement.style.backgroundColor =
                    hostConfig.cardHostBackgroundColor;
            }

            this.cardContainer.style.maxWidth = maxWidth;
            if (align === 'center') {
                this.cardContainer.style.margin = 'auto';
            }
        }
    };
}

const setThemeStylesheet = (themeName: string, stylesheetUrl: string) => {
    if (!document.querySelector(`#ac-${themeName}-styles`)) {
        // load style sheet
        const link = document.createElement('link');
        link.type = 'text/css';
        link.id = `#ac-${themeName}-styles`;
        link.rel = 'stylesheet';
        link.href = stylesheetUrl;
        document.head.appendChild(link);
    }
};

const getAllInputs = (action: Adaptive.Action) => {
    if (!action.parent) {
        return action.getAllInputs();
    } else {
        const allReferencedInputs = action.getReferencedInputs();
        return Object.values(allReferencedInputs).map(input => {
            return input;
        });
    }
};
